'use client';

import Carousel from '@/components/blocks/Carousel';
import { FilmListItem } from '@/components/blocks/FilmCard';
import ArrowLink from '@/components/ui/ArrowLink';
import { PathEnum } from '@/i18n/locale-config';
import { getDate, getWeekdayName } from '@/utils/date-format';
import { getMontrealNow, parseAsMontrealTime } from '@/utils/date-format-montreal';
import { useLocale, useTranslations } from 'next-intl';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import type { SortOption } from '../../films/components/FilmSorting';
import PaginatedFilmList from '../../films/components/PaginatedFilmList';
import { Film, ScreeningsByDateResponse, ShortProgramme } from '../types';
import type { ScheduleSortOption } from './ScheduleSorting';

interface ScheduleListProps {
    screenings: ScreeningsByDateResponse;
    filters?: ReactNode;
    sortOption?: ScheduleSortOption;
}

const ScheduleList = (props: ScheduleListProps) => {
    const { screenings, filters, sortOption = 'date' } = props;
    const [dates, setDates] = useState<string[]>([]);
    const [selectedDate, setSelectedDate] = useState<string>('');
    const [shortProgrammes, setShortProgrammes] = useState<ShortProgramme[]>([]);
    const [films, setFilms] = useState<Film[]>([]);
    const t = useTranslations();
    const locale = useLocale();

    const screeningsByDate = useMemo(() => {
        const result: Record<string, { films: Set<Film>; shortProgrammes: Set<ShortProgramme> }> =
            {};

        screenings.forEach((screening) => {
            const date = getDate(screening.start_time);

            if (!date) {
                return;
            }

            if (!result[date]) {
                result[date] = { films: new Set(), shortProgrammes: new Set() };
            }

            if (screening.films && screening.films.length > 0) {
                screening.films.forEach((film) => {
                    result[date].films.add(film);
                });
            }

            if (screening.short_programmes && screening.short_programmes.length > 0) {
                screening.short_programmes.forEach((sp) => {
                    result[date].shortProgrammes.add(sp);
                });
            }
        });

        return result;
    }, [screenings]);

    const getFilmsAndShortProgrammesByDate = (date: string) => {
        const films = Array.from(screeningsByDate[date]?.films || []);
        const shortProgrammes = Array.from(screeningsByDate[date]?.shortProgrammes || []);

        const sortByStartTime = <T extends { screenings: { start_time: string }[] }>(items: T[]) =>
            items.sort((a, b) => {
                const aScreening = a.screenings.find((s) => getDate(s.start_time) === date);
                const bScreening = b.screenings.find((s) => getDate(s.start_time) === date);

                const aStartTime = new Date(aScreening?.start_time || 0).getTime();
                const bStartTime = new Date(bScreening?.start_time || 0).getTime();

                return aStartTime - bStartTime;
            });

        const sortAlphabetically = <T extends { title?: string; name?: string }>(items: T[]) =>
            items.sort((a, b) => {
                const aLabel = 'title' in a ? a.title : a.name;
                const bLabel = 'title' in b ? b.title : b.name;
                return (aLabel || '').localeCompare(bLabel || '', locale, { sensitivity: 'base' });
            });

        const sortedFilms =
            sortOption === 'alphabetical' ? sortAlphabetically(films) : sortByStartTime(films);
        const sortedShortProgrammes =
            sortOption === 'alphabetical'
                ? sortAlphabetically(shortProgrammes)
                : sortByStartTime(shortProgrammes);

        return { sortedFilms, sortedShortProgrammes };
    };

    useEffect(() => {
        const dateKeys = Object.keys(screeningsByDate);

        const filteredDates = dateKeys.filter(
            (date) =>
                screeningsByDate[date].films.size > 0 ||
                screeningsByDate[date].shortProgrammes.size > 0,
        );

        setDates(filteredDates);

        // If current selected date has no content after filtering, select the first available date
        if (filteredDates.length > 0) {
            let targetDate = selectedDate;

            // If selected date is empty or doesn't exist in filtered dates, pick the first available
            if (!selectedDate || !filteredDates.includes(selectedDate)) {
                targetDate = filteredDates[0];
                setSelectedDate(targetDate);
            }

            const { sortedFilms, sortedShortProgrammes } =
                getFilmsAndShortProgrammesByDate(targetDate);

            setFilms(sortedFilms);
            setShortProgrammes(sortedShortProgrammes);
        } else {
            // No dates have content, clear everything
            setFilms([]);
            setShortProgrammes([]);
            if (selectedDate) {
                setSelectedDate('');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [screeningsByDate, sortOption]);

    const handleDateClick = (date: string) => {
        setSelectedDate(date);

        const { sortedFilms, sortedShortProgrammes } = getFilmsAndShortProgrammesByDate(date);

        setFilms(sortedFilms);
        setShortProgrammes(sortedShortProgrammes);
    };

    return (
        <>
            <div className="my-10 flex gap-2 overflow-x-auto pb-1">
                {dates &&
                    dates.map((date) => (
                        <button
                            key={date}
                            className={`
                  flex justify-center items-center gap-1 px-1.5 py-1 text-[46px] font-trade-gothic font-bold uppercase border leading-none
                  ${selectedDate === date ? 'bg-primary border-transparent' : ' border-disabled'}`}
                            onClick={() => handleDateClick(date)}
                        >
                            <span className="[writing-mode:vertical-rl] rotate-180 font-normal text-[28px]">
                                {getWeekdayName(
                                    screenings.find(
                                        (screening) => getDate(screening.start_time) === date,
                                    )?.start_time || '',
                                    locale,
                                )}
                            </span>
                            {date.split(' ').pop()}.
                        </button>
                    ))}
            </div>

            {/* Filters positioned below the dates */}
            {filters}

            {/* No results message */}
            {dates.length === 0 && (
                <div className="text-center py-20">
                    <p className="text-gray-200 text-xl font-helvetica-neue">
                        {t('SchedulePage.noScreenings')}
                    </p>
                </div>
            )}

            {shortProgrammes.map((shortProgramme) => {
                const now = getMontrealNow();
                const filmSlides = shortProgramme.films
                    .map((film: any) => {
                        // Filter screenings to exclude those with ticket_sales_disabled and past screenings (except ONLINE)
                        const filteredScreenings = (film.screenings || []).filter(
                            (screening: any) => {
                                if (screening.ticket_sales_disabled === true) return false;
                                if (screening.screening_type === 'ONLINE') return true;
                                if (!screening.start_time) return false;
                                const screeningTime = parseAsMontrealTime(screening.start_time);
                                return (
                                    !isNaN(screeningTime.getTime()) &&
                                    screeningTime.getTime() > now.getTime()
                                );
                            },
                        );
                        const filteredShortProgrammeScreenings = (
                            shortProgramme.screenings || []
                        ).filter((screening: any) => {
                            if (screening.ticket_sales_disabled === true) return false;
                            if (screening.screening_type === 'ONLINE') return true;
                            if (!screening.start_time) return false;
                            const screeningTime = parseAsMontrealTime(screening.start_time);
                            return (
                                !isNaN(screeningTime.getTime()) &&
                                screeningTime.getTime() > now.getTime()
                            );
                        });

                        return {
                            ...film,
                            screenings: filteredScreenings,
                            short_programme: {
                                ...shortProgramme,
                                screenings: filteredShortProgrammeScreenings,
                            },
                        };
                    })
                    .sort((a: any, b: any) =>
                        a.title.localeCompare(b.title, locale, { sensitivity: 'base' }),
                    );
                return (
                    <div className="flex gap-5 flex-col mt-10 mb-20" key={shortProgramme.id}>
                        <div className="flex justify-between items-baseline border-b border-white/50 pb-2">
                            <h3 className="text-h4 lg:text-h1 font-bold leading-none">
                                {shortProgramme.name}
                            </h3>
                            <ArrowLink
                                className="max-lg:absolute max-lg:bottom-0 max-lg:right-0 max-lg:mr-5"
                                label={t('Common.showAll')}
                                href={`${PathEnum.ShortPrograms}/${shortProgramme?.slug}`}
                            />
                        </div>
                        <p className="font-helvetica-neue text-[14px] lg:text-[22px] font-light text-gray-200 leading-none">
                            {shortProgramme.description}
                        </p>
                        <div className="mt-5 max-lg:hidden w-[calc(100%+20px)]">
                            <Carousel slides={filmSlides} slidesPerView={2.1} filmsSlider />
                        </div>
                        <div className="mt-2 w-full lg:hidden">
                            <Carousel slides={filmSlides} slidesPerView={1} filmsSlider />
                        </div>
                    </div>
                );
            })}

            <PaginatedFilmList
                films={films
                    .map((film) => {
                        // Filter screenings to exclude those with ticket_sales_disabled and past screenings (except ONLINE)
                        const now = getMontrealNow();
                        const filteredScreenings = (film.screenings || []).filter((screening) => {
                            if (screening.ticket_sales_disabled === true) return false;
                            if (screening.screening_type === 'ONLINE') return true;
                            if (!screening.start_time) return false;
                            const screeningTime = parseAsMontrealTime(screening.start_time);
                            return (
                                !isNaN(screeningTime.getTime()) &&
                                screeningTime.getTime() > now.getTime()
                            );
                        });

                        return {
                            ...film,
                            screenings: filteredScreenings,
                        };
                    })
                    .map((film) => film as unknown as FilmListItem)}
                sorting
                sortOption={sortOption as SortOption}
            />
        </>
    );
};

export default ScheduleList;
